import {resolveMustCreatePasswordFromLoginInfo} from 'app/modules/auth/selective/mustCreatePasswordHelpers'
import {isDevEnv} from 'common/HelperFunctions'
import {USER_TYPES} from 'common/constants'
import localforage from 'localforage'

import {setLanguage} from '_metronic/i18n/Metronici18n'

import {REDUX_SAVE_SCHOOL_USER_PROFILE} from '../Actions/School/User/ActionTypes/ReduxActionTypes'
import {
  REDUX_DESTROY_SESSION,
  REDUX_SAVE_LOGIN_INFO,
  REDUX_SET_ACCESS_TOKEN,
  REDUX_SET_MUST_CREATE_PASSWORD,
} from '../Actions/SignIn/ActionTypes/ReduxActionTypes'
import {
  REDUX_IMPERSONATE,
  REDUX_PROFILE_PICTURE_UPDATE,
  REDUX_REVERT_TO_ORIGINAL_USER,
  REDUX_SAVE_AI_CREDITS,
  REDUX_SAVE_USER_SETTINGS,
  REDUX_SET_URDD_ID,
} from '../Actions/User/ActionTypes/ReduxActionTypes'

export type UserReducer = {
  isSignout: boolean
  isLoggedIn: boolean | undefined
  accessToken: string | null
  tenant: string
  user: {[key: string]: any} | null
  userType: string
  isAdmissionUser: boolean
  mustCreatePassword: boolean
  appSettings: {[key: string]: any} | null
  settings: {[key: string]: any} | null
  urddId: string | null
  permissions: string[]
  impersonation: {[key: string]: any} | null
  aiCredits: {
    total_credits: number | null
    remaining_credits: number | null
    credits_per_use: number | null
  }
}

const initialState: UserReducer = {
  isSignout: false,
  isLoggedIn: undefined,
  accessToken: null,
  tenant: '',
  user: null,
  userType: '',
  isAdmissionUser: false,
  mustCreatePassword: false,
  appSettings: null,
  settings: null,
  urddId: null,
  permissions: [],
  impersonation: null,
  aiCredits: {
    total_credits: null,
    remaining_credits: null,
    credits_per_use: null,
  },
}

const clearFeedbackSurveyDismissal = (accessToken: string | null) => {
  if (!accessToken) return

  try {
    sessionStorage.removeItem(`feedback_survey_dismissed_${accessToken.slice(0, 20)}`)
  } catch {
    // ignore storage errors
  }
}

const reducer = (state = initialState, action: any) => {
  switch (action.type) {
    case REDUX_SAVE_LOGIN_INFO: {
      const {
        accessToken = null,
        tenant = '',
        user = null,
        userType = '',
        applicationSettings = null,
        permissions,
        promptChangePassword,
        urddId = null,
        urdd_id = null,
      } = action.payload.result

      const mustCreatePassword = resolveMustCreatePasswordFromLoginInfo(
        state.mustCreatePassword,
        action
      )

      if (promptChangePassword) {
        return {
          ...state,
          accessToken,
          tenant,
          mustCreatePassword,
        }
      }

      // Parents are still served by the legacy portal in production (see
      // handlePostLoginNavigation), so don't persist their session here.
      // Students now use the new portal everywhere.
      // In-progress admission applicants (flagged by the admission login/signup
      // endpoints) are exempt from that prod-login block — their tokens are only
      // accepted by the admission APIs anyway.
      if (!isDevEnv() && userType === USER_TYPES.PARENT && !action.payload.result.isAdmissionUser) {
        return state
      }

      if (applicationSettings) {
        // const {rename} = applicationSettings
        // MODULES.map((mod) => {
        //   rename[mod.value] && (mod.name = rename[mod.value])
        // })
      }
      return {
        ...state,
        accessToken,
        tenant,
        user,
        userType,
        appSettings: applicationSettings,
        isLoggedIn: accessToken ? true : false,
        isSignout: false,
        permissions,
        mustCreatePassword,
        // admission tokens are only valid on the admission APIs; the saga's
        // global 401-logout must not fire for them
        isAdmissionUser: Boolean(action.payload.result.isAdmissionUser),
        // Preserve existing urddId if new payload doesn't have it
        urddId: urddId !== null ? urddId : urdd_id !== null ? urdd_id : state.urddId,
      }
    }
    case REDUX_SET_MUST_CREATE_PASSWORD:
      return {
        ...state,
        mustCreatePassword: Boolean(action.payload),
      }
    case REDUX_SET_ACCESS_TOKEN:
      return {
        ...state,
        accessToken: action.payload,
      }
    // case REDUX_SELECT_CHILD:
    //   return {
    //     ...state,
    //     selectedChild: action.payload.data,
    //   }
    case REDUX_SAVE_USER_SETTINGS: {
      const {result} = action.payload
      let lang = result.applicationSettings.language
      if (!lang) {
        lang = 'en'
      }
      setLanguage(lang)
      return {
        ...state,
        settings: result,
      }
    }
    case REDUX_DESTROY_SESSION:
      clearFeedbackSurveyDismissal(state.accessToken)
      localStorage.clear()
      localforage.clear()
      return {
        ...state,
        ...initialState,
        isSignout: true,
      }
    case REDUX_SAVE_SCHOOL_USER_PROFILE: {
      const user = {...(state?.user ? state.user : {}), ...action.payload.result}
      return {
        ...state,
        user,
      }
    }
    case REDUX_PROFILE_PICTURE_UPDATE: {
      const user = {...(state?.user ? state.user : {}), ...action.payload}
      return {
        ...state,
        user,
      }
    }
    case REDUX_IMPERSONATE: {
      const impersonation = {
        accessToken: state.accessToken,
        user: state.user,
        userType: state.userType,
        appSettings: state.appSettings,
        settings: state.settings,
        permissions: state.permissions,
      }

      return {
        ...state,
        ...action.payload,
        impersonation,
      }
    }
    case REDUX_REVERT_TO_ORIGINAL_USER:
      if (!state.impersonation) return state
      return {
        ...state,
        accessToken: state.impersonation?.accessToken,
        user: state.impersonation?.user,
        userType: state.impersonation?.userType,
        appSettings: state.impersonation?.appSettings,
        settings: state.impersonation?.settings,
        permissions: state.impersonation?.permissions,
        impersonation: null,
      }
    case REDUX_SAVE_AI_CREDITS:
      return {
        ...state,
        aiCredits: action.payload,
      }
    case REDUX_SET_URDD_ID:
      return {
        ...state,
        urddId:
          action.payload !== undefined && action.payload !== null ? String(action.payload) : null,
      }
    default:
      return state
  }
}

export default reducer
